import { Race } from "@/backend/domain/models/race";
import { ArrowDown } from "lucide-react";
import LoadingButton from "@/components/shared/other/loadingButton";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router";

type TimeLeft = {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
};

type Props = {
    race: Race;
    recentRaces: Race[];
};

export function FeaturedRace({ race, recentRaces }: Props) {
    const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

    useEffect(() => {
        const calculateTimeLeft = () => {
            const now = new Date();
            const target = new Date(race.date);
            const diff = target.getTime() - now.getTime();

            if (diff <= 0) {
                return { days: 0, hours: 0, minutes: 0, seconds: 0 };
            }

            return {
                days: Math.floor(diff / (1000 * 60 * 60 * 24)),
                hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((diff / 1000 / 60) % 60),
                seconds: Math.floor((diff / 1000) % 60)
            };
        };

        const timer = setInterval(() => {
            setTimeLeft(calculateTimeLeft());
        }, 1000);

        setTimeLeft(calculateTimeLeft());

        return () => clearInterval(timer);
    }, [race.date]);

    return (
        <section className="min-h-screen flex flex-col">
            <div className="flex-1 grid md:grid-cols-2 gap-12 px-6">
                {/* Left: Featured Race Details */}
                <div className="flex flex-col justify-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="mb-12"
                    >
                        <div className="relative">
                            <div className="absolute -left-4 top-0 w-2 h-24 bg-slate-700" />
                            <h1 className="text-4xl md:text-7xl font-light tracking-tighter leading-tight mb-4">
                                {race.name.toUpperCase()}
                            </h1>

                            {race.status === "REGISTRATION_CLOSED" && (
                                <div className="mb-6 inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-md text-sm tracking-wider">
                                    Anmeldung geschlossen
                                </div>
                            )}

                            {/* Countdown Grid */}
                            <motion.div
                                className="grid grid-cols-4 gap-4 mt-8 mb-12"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                {[
                                    { value: timeLeft.days, label: "TAGE" },
                                    { value: timeLeft.hours, label: "STD" },
                                    { value: timeLeft.minutes, label: "MIN" },
                                    { value: timeLeft.seconds, label: "SEK" }

                                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                ].map((item, index) => (
                                    <motion.div
                                        key={item.label}
                                        className="text-center p-4 bg-slate-50 rounded-lg"
                                        whileHover={{ y: -2 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <div className="text-2xl md:text-3xl font-light text-slate-900">
                                            {String(item.value).padStart(2, '0')}
                                        </div>
                                        <div className="text-xs tracking-wider text-slate-500 mt-1">
                                            {item.label}
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>

                            {/* Race Details */}
                            <div className="grid grid-cols-2 gap-8">
                                <div>
                                    <div className="text-sm tracking-widest text-slate-600 mb-2">DATUM</div>
                                    <div className="text-lg">
                                        {new Date(race.date).toLocaleDateString('de-DE', {
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric',
                                        })}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm tracking-widest text-slate-600 mb-2">ORT</div>
                                    <div className="text-lg">{race.location}</div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {race.status === "REGISTRATION_OPEN" ? (
                        <LoadingButton
                            action={`/register/${race.id}/register-type`}
                            className="w-fit px-8 py-6 bg-gradient-to-r from-slate-800 to-slate-700 text-white
                                     hover:from-slate-900 hover:to-slate-800 font-light tracking-wider shadow-lg
                                     hover:shadow-xl transition-all duration-300"
                            loadingText="LADEN..."
                        >
                            ANMELDUNG
                        </LoadingButton>
                    ) : (
                        <Button
                            asChild
                            className="w-fit px-8 py-6 bg-gradient-to-r from-slate-800 to-slate-700 text-white
                                     hover:from-slate-900 hover:to-slate-800 font-light tracking-wider shadow-lg
                                     hover:shadow-xl transition-all duration-300"
                        >
                            <Link to={`/race/${race.id}`}>
                                ZUR RENNDETAILS →
                            </Link>
                        </Button>
                    )}
                </div>

                {/* Right: Recent Results */}
                <div className="flex items-center">
                    <motion.div
                        className="w-full space-y-6"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                    >
                        <h2 className="text-sm tracking-widest text-slate-600">LETZTE ERGEBNISSE</h2>
                        <div className="space-y-4">
                            {recentRaces.map((recentRace, index) => (
                                <motion.a
                                    key={recentRace.id}
                                    href={`/race/${recentRace.id}`}
                                    className="block group"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                                >
                                    <div className="p-6 bg-white border border-slate-100 rounded-lg
                                                  group-hover:bg-slate-50 transition-all duration-200">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h3 className="font-medium mb-1">{recentRace.name}</h3>
                                                <p className="text-sm text-slate-500">
                                                    {new Date(recentRace.date).toLocaleDateString('de-DE', {
                                                        day: 'numeric',
                                                        month: 'long'
                                                    })}
                                                </p>
                                            </div>
                                            <span className="text-sm text-slate-400 group-hover:text-slate-800
                                                         transition-colors duration-200">
                                                Ergebnisse →
                                            </span>
                                        </div>
                                    </div>
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Scroll Indicator */}
            <motion.div
                className="p-6 flex justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 1 }}
            >
                <ArrowDown className="animate-bounce text-slate-400 h-6 w-6" />
            </motion.div>
        </section>
    );
}
